import { Stack } from "@mui/material";
import { useRef, useState } from "react";
import NumberTextField from "@entrilia/shared/components/inputs/NumberTextField";
import useDebounce from "@entrilia/shared/hooks/useDebounce";
import type { FilterValue, NumberFilter, NumberFilterOperator } from "../filterTypes";
import { numberFilterOperatorOptions } from "../handlers/numberFilter";
import EmptyFilterBody from "./EmptyFilterBody";
import OperatorSelect from "./OperatorSelect";

interface Props {
  filter: NumberFilter;
  onUpdateValue: (newValue: FilterValue) => void;
}

const isEmptyFilterBody = (operator: NumberFilterOperator) => ["empty", "not_empty"].includes(operator);
const hasTwoValues = (operator: NumberFilterOperator) => operator === "between";

const getOperatorOptions = (filter: NumberFilter) =>
  filter.operatorOptions !== undefined
    ? numberFilterOperatorOptions.filter((option) => filter.operatorOptions?.includes(option.value))
    : numberFilterOperatorOptions;

const NumberFilterEditor = ({ filter, onUpdateValue }: Props) => {
  const firstInputRef = useRef<HTMLInputElement>(null);

  const [firstValue, setFirstValue] = useState(filter.value.number);
  const [secondValue, setSecondValue] = useState(filter.value.secondNumber);

  const handleOperatorChange = (operator: NumberFilterOperator) => {
    if (isEmptyFilterBody(operator)) {
      setFirstValue(undefined);
      setSecondValue(undefined);
      onUpdateValue({ operator, number: undefined, secondNumber: undefined });
    } else {
      setSecondValue(undefined);
      onUpdateValue({ operator, number: filter.value.number, secondNumber: undefined });
    }

    setTimeout(() => firstInputRef.current?.focus(), 100);
  };

  const updateNumberValuesInFilter = useDebounce((number: number | undefined, secondNumber: number | undefined) => {
    onUpdateValue({
      operator: filter.value.operator,
      number,
      secondNumber,
    });
  }, 500);

  const handleFirstValueChange = (value: number | undefined) => {
    setFirstValue(value);
    updateNumberValuesInFilter(value, filter.value.secondNumber);
  };

  const handleSecondValueChange = (value: number | undefined) => {
    setSecondValue(value);
    updateNumberValuesInFilter(filter.value.number, value);
  };

  const emptyFilterBody = isEmptyFilterBody(filter.value.operator);
  const showTwoInputs = hasTwoValues(filter.value.operator);
  const operatorOptions = getOperatorOptions(filter);

  return (
    <Stack spacing={2} sx={[{ p: 2 }, (t) => ({ width: t.spacing(47.5) })]}>
      <OperatorSelect<NumberFilterOperator>
        filterName={filter.name}
        options={operatorOptions}
        value={filter.value.operator}
        onChange={handleOperatorChange}
      />
      {emptyFilterBody && <EmptyFilterBody />}
      {!emptyFilterBody && (
        <Stack direction="row" spacing={1}>
          <NumberTextField
            inputRef={firstInputRef}
            autoFocus
            fullWidth
            clearable
            selectOnFocus
            numberValue={firstValue}
            onNumberChange={handleFirstValueChange}
          />
          {showTwoInputs && (
            <NumberTextField
              fullWidth
              clearable
              selectOnFocus
              numberValue={secondValue}
              onNumberChange={handleSecondValueChange}
            />
          )}
        </Stack>
      )}
    </Stack>
  );
};

export default NumberFilterEditor;
